import { useEffect, useRef, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import {
  FeedbackSkillsStreamingEvent,
  useFeedbackSkillsStreaming,
} from '@/context/channels/guidance-feedback/streaming';
import { useShowUserFeedback } from '@/data/guidance/feedback';
import { useUserPrioritySkillSetsIndex } from '@/data/taxonomy/user-priority-skill-sets';
import { loadingMessages } from '@/packages/skill-management/components/feedbacks/processing-feedback/loading-messages';

const messageIntervalInMs = 4000;

const preloadAssets = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  loadingMessages.map((step) => {
    const img = new Image();
    img.src = step.iconUrl as string;
  });
};

interface ProcessingFeedbackProps {
  centered?: boolean;
  feedbackId?: number;
  onCompleted?: () => void;
  onError?: () => void;
}

export const ProcessingFeedback = ({
  centered = false,
  feedbackId,
  onCompleted,
  onError,
}: ProcessingFeedbackProps) => {
  const { refetch: refetchUserFeedback } = useShowUserFeedback();
  const { refetch: refetchSelectedSkills } = useUserPrioritySkillSetsIndex();
  const [sequenceIndex, setSequenceIndex] = useState(0);
  const hasStartedRef = useRef(false);

  const { status, startStreaming } = useFeedbackSkillsStreaming({
    onCompleted: async () => {
      await refetchUserFeedback();
      await refetchSelectedSkills();
      onCompleted?.();
    },
    onError,
  });

  const currentMessage = loadingMessages[sequenceIndex];
  const { title, iconUrl } = currentMessage;

  const error = status === FeedbackSkillsStreamingEvent.Error;
  const style = centered ? { md: 'center' } : undefined;

  useEffect(() => {
    // preload assets so the images don't flicker when changing
    preloadAssets();
  }, []);

  useEffect(() => {
    if (feedbackId && !hasStartedRef.current) {
      hasStartedRef.current = true;
      startStreaming(feedbackId);
    }
  }, [feedbackId, startStreaming]);

  useEffect(() => {
    // iterate over the first 3 messages
    const interval = setInterval(() => {
      setSequenceIndex((prevIndex) => (prevIndex < 2 ? prevIndex + 1 : 0));
    }, messageIntervalInMs);

    // display last message on error
    if (error) {
      clearInterval(interval);
      setSequenceIndex(4);
    }

    return () => {
      clearInterval(interval);
    };
  }, [error]);

  return (
    <Stack
      gap={4}
      textAlign={{ md: centered ? 'center' : undefined }}
      justifyContent={style}
      alignItems={style}
    >
      <Box component="img" src={iconUrl} sx={{ height: 64, width: 64 }} />
      <Stack gap={2} minHeight={80}>
        <Typography variant="h3">{title}</Typography>
        <Typography variant="body1">{currentMessage.message}</Typography>
      </Stack>
    </Stack>
  );
};
